import { getNoNameFiltersData } from '@/pages/intelligence/services/filter';
import { fetchCompaniesRanksData, fetchCompaniesTrendsData } from '@/pages/intelligence/services/topcharts';
import { tranFormRevenueRangeParams } from '@/pages/intelligence/pages/TopCharts/Companies/utils';
import type { RanksResponse, TrendsResponse } from '@/pages/intelligence/pages/_config/topchartsInterface';
import type {
  ITopChartsCompaniesFetchParams,
  ITopChartsCompaniesRank,
  ITopChartsCompaniesTrend,
  ITopChartsCompaniesTimeline,
} from '@/pages/intelligence/pages/TopCharts/Companies/components/CompaniesCore/types';

type ITopChartsCompaniesRanksResponse = RanksResponse<ITopChartsCompaniesRank>;

type ITopChartsCompaniesTrendsResponse = TrendsResponse<ITopChartsCompaniesTrend, ITopChartsCompaniesRank>;

export const getSpecialArrValue = (values: string[], options: any[]): string[] => {
  if (!Array.isArray(values)) return [];
  if (!Array.isArray(options)) return values;
  return values?.length === options?.length ? [] : values;
};

export const fetchTopChartsCompaniesRanks = async (
  params: ITopChartsCompaniesFetchParams,
  cfg: {
    headQuatersOptions: any[];
    signal: any
  },
): Promise<ITopChartsCompaniesRanksResponse> => {
  const { headQuatersOptions, signal } = cfg || {};
  const ranksRes: ITopChartsCompaniesRanksResponse = {
    list: [],
    total: 0,
    req: {
      start_date: '',
      end_date: '',
    },
  };
  try {
    const newParams = tranFormRevenueRangeParams(params);
    const { code, data, req } = await fetchCompaniesRanksData({
      ...newParams,
      company_headquarter: getSpecialArrValue(params?.company_headquarter, headQuatersOptions),
    }, {signal});
    if (code !== 0) return ranksRes;
    const { ranklist: ranks, total } = data ?? {};
    if (!Array.isArray(ranks)) return ranksRes;

    ranksRes.list = ranks;
    ranksRes.total = total ?? ranksRes.list.length;
    ranksRes.req = {
      start_date: req?.start_date ?? '',
      end_date: req?.end_date ?? '',
    };
  } catch (exception) {
    console.warn(exception);
    ranksRes.total = 0;
    ranksRes.list = [];
    ranksRes.req = {
      date_type: params?.granularity ?? 'daily',
      start_date: params?.start_date ?? '',
      end_date: params?.end_date ?? '',
    };
    ranksRes.error = exception as any
  }
  return ranksRes;
};

const handleTimelines = (data: any[]): ITopChartsCompaniesTimeline[] => {
  if (!Array.isArray(data)) return [];
  return data.map(mapTimeline);
};

const mapTimeline = (data: any): ITopChartsCompaniesTimeline => {
  const { time, time_line: metrics } = data ?? {};
  return {
    time: time ?? '',
    metric_value_list: metrics ?? [],
  };
};

const mapTrend = (data: any): ITopChartsCompaniesTrend => {
  const { rank, app_id: appId, id, metric_value_list: timelines } = data ?? {};
  const idTmp = appId ?? id ?? '';
  return {
    ...data,
    rank: rank ?? Number.NaN,
    app_id: idTmp,
    id: idTmp,
    time_line: handleTimelines(timelines),
  };
};

export const fetchTopChartsCompaniesTrends = async (
  params: ITopChartsCompaniesFetchParams,
  cfg: {
    headQuatersOptions: any[];
  },
): Promise<ITopChartsCompaniesTrendsResponse> => {
  const { headQuatersOptions } = cfg || {};
  const trendsRes: ITopChartsCompaniesTrendsResponse = {
    list: [],
    barList: [],
  };
  try {
    const newParams = tranFormRevenueRangeParams(params);
    const { code, data } = await fetchCompaniesTrendsData({
      ...newParams,
      company_headquarter: getSpecialArrValue(params?.company_headquarter, headQuatersOptions),
    });
    if (code !== 0) return trendsRes;
    const { metric_timeline_list: trends } = data ?? {};
    if (!Array.isArray(trends)) return trendsRes;

    trendsRes.list = trends.map(mapTrend);
  } catch (exception) {
    console.warn(exception);
    trendsRes.list = [];
  }
  return trendsRes;
};

export const fetchGenresAndHeadQuaters = async (): Promise<any> => {
  const result = {
    annir: [],
    country: [],
  };
  try {
    const res = await getNoNameFiltersData({ type: [{ tabName: 'company' }] });
    const annirOptions = res?.data?.annir?.map((item: any) => {
      const annSubOptions = item?.ann_sub?.filter((subItem: string) => subItem !== 'Others');
      return {
        ...item,
        ann_sub: annSubOptions ?? [],
      };
    });
    result.annir = annirOptions ?? [];
    result.country = res?.data?.country ?? [];
  } catch (exception) {
    console.warn(exception);
  }
  return result;
};

export const fetchTopChartsCompaniesRanksSearch = async (
  params: ITopChartsCompaniesFetchParams,
  cfg: {
    headQuatersOptions: any[];
    signal: any
  },
): Promise<ITopChartsCompaniesRanksResponse> => {
  const res = await fetchTopChartsCompaniesRanks(params, cfg);
  const { list } = res ?? {};
  const listTmp = list?.map((item) => {
    const { rank, app_id: id, name, image_url: cover, location_identifiers_country: publisherAddr } = item ?? {};
    return {
      ...item,
      rank: rank ?? Number.NaN,
      id: id ?? '',
      name: name ?? '',
      cover: cover ?? '',
      publisher_addr: publisherAddr ?? '',
    };
  });
  return {
    ...res,
    list: listTmp ?? [],
  }
};
